/**
 * Module 1 — Classification review panel.
 *
 * Shows the deal's SOC-classified line items DEDUPED by distinct label, needs-confirm +
 * biggest-dollar first. The goal is single-digit active confirmations: the materiality gate
 * already auto-posted immaterial low-confidence lines, so this surfaces only the lines that
 * move the numbers. Confirming a label resolves every occurrence and teaches the firm lexicon.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { classificationApi } from '../services/api';

// Compact SOC leaf list for the override dropdown (mirrors backend taxonomy.js).
const SOC_OPTIONS = [
  ['R-01', 'Revenue · gross_revenue'], ['R-02', 'Revenue · refunds_and_credits'],
  ['R-03', 'Revenue · net_patient_revenue'], ['R-04', 'Revenue · grant_revenue'],
  ['R-05', 'Revenue · contract_revenue'], ['R-06', 'Revenue · ancillary_revenue'],
  ['R-07', 'Revenue · other_income'], ['R-08', 'Revenue · total_net_revenue'],
  ['CR-01', 'COGS · direct_medications'], ['CR-02', 'COGS · direct_clinical_supplies'],
  ['CR-03', 'COGS · direct_lab_costs'], ['CR-04', 'COGS · total_cost_of_revenue'],
  ['PE-01', 'Personnel · owner_officer_compensation'], ['PE-02', 'Personnel · physician_compensation'],
  ['PE-03', 'Personnel · clinical_staff_wages'], ['PE-04', 'Personnel · admin_staff_wages'],
  ['PE-05', 'Personnel · payroll_taxes'], ['PE-06', 'Personnel · health_insurance'],
  ['PE-07', 'Personnel · retirement_plan'], ['PE-08', 'Personnel · other_benefits'],
  ['PE-09', 'Personnel · total_personnel_expense'],
  ['OC-01', 'Occupancy · rent_and_cam'], ['OC-02', 'Occupancy · utilities'],
  ['OC-03', 'Occupancy · repairs_and_maintenance'], ['OC-04', 'Occupancy · equipment_leases'],
  ['OC-05', 'Occupancy · property_taxes'], ['OC-06', 'Occupancy · total_occupancy'],
  ['OE-01', 'Opex · technology_and_emr'], ['OE-02', 'Opex · medical_supplies'],
  ['OE-03', 'Opex · legal_and_accounting'], ['OE-04', 'Opex · marketing_and_advertising'],
  ['OE-05', 'Opex · insurance_malpractice'], ['OE-06', 'Opex · insurance_other'],
  ['OE-07', 'Opex · bank_and_merchant_fees'], ['OE-08', 'Opex · outside_services'],
  ['OE-09', 'Opex · recruitment'], ['OE-10', 'Opex · dues_and_subscriptions'],
  ['OE-11', 'Opex · depreciation'], ['OE-12', 'Opex · amortization'],
  ['OE-13', 'Opex · interest_expense'], ['OE-14', 'Opex · other_operating'],
  ['OE-15', 'Opex · total_operating_expense'],
  ['EB-01', 'EBITDA · unadjusted_ebitda'], ['EB-02', 'EBITDA · total_addbacks'],
  ['EB-03', 'EBITDA · adjusted_ebitda'],
];

const ACCENT = '#161f4a';
const money = (n) => (n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** Persistent banner telling the user whether classification has already run, and when. */
function StatusBanner({ status }) {
  if (!status) return null;
  const done = status.processed || 0;
  const total = status.documents || 0;
  let tone;
  let text;
  if (status.status === 'complete') {
    tone = { bg: '#f0fdf4', border: '#bbf7d0', color: '#15803d' };
    text = `✓ Financials classified — all ${total} document${total === 1 ? '' : 's'} processed${status.classified_at ? ` on ${fmtWhen(status.classified_at)}` : ''}.`;
  } else if (status.status === 'in_progress') {
    tone = { bg: '#eff6ff', border: '#bfdbfe', color: '#1d4ed8' };
    text = status.message || `Classifying… (${done} of ${total} documents done)`;
  } else if (status.status === 'stale') {
    tone = { bg: '#fffbeb', border: '#fde68a', color: '#b45309' };
    text = `⚠ ${done} of ${total} document${total === 1 ? '' : 's'} classified — some changed or failed to process. Re-classify to finish.`;
  } else {
    return null;
  }
  return (
    <div style={{ background: tone.bg, border: `1px solid ${tone.border}`, color: tone.color, borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
      {text}
    </div>
  );
}

export default function ClassificationReview({ dealId }) {
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [overrides, setOverrides] = useState({});   // normalized_label -> soc_tag
  const [error, setError] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);

  const loadLines = useCallback(async () => {
    const res = await classificationApi.lines(dealId);
    setData(res.data);
  }, [dealId]);

  // Poll classification status every 2 seconds while in_progress.
  const startPolling = useCallback(() => {
    if (pollInterval) return;
    const id = setInterval(async () => {
      try {
        const res = await classificationApi.status(dealId);
        setStatus(res.data);
        if (res.data.status === 'complete') {
          clearInterval(id);
          setPollInterval(null);
          await loadLines();
        }
      } catch (e) { /* ignore polling errors */ }
    }, 2000);
    setPollInterval(id);
  }, [dealId, loadLines, pollInterval]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await classificationApi.status(dealId);
        if (!alive) return;
        setStatus(res.data);
        if (res.data.status === 'complete') await loadLines();
        else if (res.data.status === 'in_progress') startPolling();
      } catch (e) { setError('Could not load classification status.'); }
    })();
    return () => { alive = false; };
  }, [dealId, loadLines, startPolling]);

  useEffect(() => {
    return () => { if (pollInterval) clearInterval(pollInterval); };
  }, [pollInterval]);

  const runClassify = async (force = false) => {
    setBusy(true); setError(null);
    try {
      const runRes = await classificationApi.run(dealId, force);
      // Backend returns immediately; start polling for progress.
      if (runRes.data.started || runRes.data.alreadyRunning) {
        startPolling();
      } else if (runRes.data.skipped) {
        setStatus({ status: 'none', documents: 0, processed: 0, message: 'No financial documents to classify.' });
      }
      // Refresh status once immediately so the UI shows the new state.
      const s = await classificationApi.status(dealId);
      setStatus(s.data);
    } catch (e) {
      setError('Classification failed to start. Try again.');
    } finally { setBusy(false); }
  };

  const confirmLabels = async (labels) => {
    const items = labels.map(l => ({ raw_label: l.raw_label, soc_tag: overrides[l.normalized_label] || l.soc_tag }))
      .filter(it => it.soc_tag);
    if (items.length === 0) return;
    setBusy(true);
    try {
      await classificationApi.confirm(dealId, items);
      await loadLines();
      setOverrides({});
    } catch (e) { setError('Confirm failed.'); }
    finally { setBusy(false); }
  };

  if (error) return <div style={{ padding: 16, color: '#b91c1c' }}>{error}</div>;

  // Preparing state — lazy classify-on-open.
  if (!data) {
    const needsRun = !status || status.status === 'none' || status.status === 'stale';
    const isRunning = status && status.status === 'in_progress';
    const pct = status && status.documents > 0
      ? Math.round(((status.processed || 0) / status.documents) * 100)
      : 0;

    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <h3 style={{ marginBottom: 8 }}>Financial classification</h3>
        {status && status.documents === 0 ? (
          <p style={{ color: '#64748b' }}>No spreadsheet or financial documents to classify on this deal yet.</p>
        ) : isRunning ? (
          <>
            <p style={{ color: '#64748b', marginBottom: 12, fontSize: 14 }}>
              {status.message || 'Preparing financials…'}
            </p>
            <div style={{ width: '100%', maxWidth: 360, margin: '0 auto 16px', background: '#e2e8f0', borderRadius: 6, height: 10, overflow: 'hidden' }}>
              <div style={{
                width: `${pct}%`,
                height: '100%',
                background: ACCENT,
                borderRadius: 6,
                transition: 'width 0.5s ease',
              }} />
            </div>
            <p style={{ color: '#94a3b8', fontSize: 12 }}>
              {status.processed || 0} of {status.documents} documents processed
            </p>
          </>
        ) : needsRun ? (
          <>
            <p style={{ color: '#64748b', marginBottom: 16 }}>
              Classify this deal's financial line items to a standard chart of accounts.
            </p>
            <button onClick={() => runClassify(false)} disabled={busy}
              style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer' }}>
              {busy ? 'Starting…' : 'Prepare financials'}
            </button>
          </>
        ) : status && status.status === 'complete' ? (
          // Classification ran, but no tagged lines came back (all extraction was data-dump
          // noise, or the line read failed). Don't strand the user on a fake spinner — tell
          // them it ran and offer a re-run.
          <>
            <p style={{ color: '#64748b', marginBottom: 16 }}>
              ✓ Classification has already run for this deal, but no financial line items were
              tagged{status.documents ? ` across ${status.documents} document(s)` : ''}. The source
              files may not contain a recognizable financial statement.
            </p>
            <button onClick={() => runClassify(true)} disabled={busy}
              style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer' }}>
              {busy ? 'Starting…' : 'Re-classify'}
            </button>
          </>
        ) : (
          <p style={{ color: '#64748b' }}>Preparing financials…</p>
        )}
      </div>
    );
  }

  const needs = data.labels.filter(l => l.needs_confirm);
  const resolved = data.labels.filter(l => !l.needs_confirm);

  return (
    <div style={{ padding: 16 }}>
      <StatusBanner status={status} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <strong>{data.total_labels}</strong> distinct labels ·{' '}
          <strong style={{ color: needs.length ? '#b45309' : '#15803d' }}>{data.needs_confirm_count}</strong> need confirmation ·{' '}
          {data.auto_posted_count} auto-posted
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {needs.length > 0 && (
            <button onClick={() => confirmLabels(needs)} disabled={busy}
              style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>
              Confirm all {needs.length} proposals
            </button>
          )}
          <button onClick={() => runClassify(true)} disabled={busy}
            style={{ background: '#fff', color: ACCENT, border: `1px solid ${ACCENT}`, borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>
            Re-classify
          </button>
        </div>
      </div>

      {needs.length > 0 && <h4 style={{ margin: '12px 0 6px' }}>Needs confirmation (material, ambiguous)</h4>}
      <LabelTable rows={needs} overrides={overrides} setOverrides={setOverrides}
        onConfirm={(l) => confirmLabels([l])} busy={busy} highlight />

      <h4 style={{ margin: '18px 0 6px', color: '#64748b' }}>Auto-resolved ({resolved.length})</h4>
      <LabelTable rows={resolved} overrides={overrides} setOverrides={setOverrides}
        onConfirm={(l) => confirmLabels([l])} busy={busy} />
    </div>
  );
}

function LabelTable({ rows, overrides, setOverrides, onConfirm, busy, highlight }) {
  if (rows.length === 0) return null;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ textAlign: 'left', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
          <th style={{ padding: '6px 8px' }}>Label</th>
          <th style={{ padding: '6px 8px' }}>Proposed SOC</th>
          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Max $</th>
          <th style={{ padding: '6px 8px', textAlign: 'right' }}>#</th>
          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Conf.</th>
          <th style={{ padding: '6px 8px' }}></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((l) => (
          <tr key={l.normalized_label}
            style={{ borderBottom: '1px solid #f1f5f9', background: highlight ? '#fffbeb' : 'transparent' }}>
            <td style={{ padding: '6px 8px' }}>
              {l.raw_label}
              {l.review_later && <span style={{ marginLeft: 6, fontSize: 11, color: '#94a3b8' }}>(review later)</span>}
            </td>
            <td style={{ padding: '6px 8px' }}>
              <select value={overrides[l.normalized_label] || l.soc_tag || ''}
                onChange={(e) => setOverrides({ ...overrides, [l.normalized_label]: e.target.value })}
                style={{ padding: '4px 6px', borderRadius: 6, border: '1px solid #cbd5e1', maxWidth: 260 }}>
                <option value="">— unmapped —</option>
                {SOC_OPTIONS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </td>
            <td style={{ padding: '6px 8px', textAlign: 'right' }}>{money(l.max_amount)}</td>
            <td style={{ padding: '6px 8px', textAlign: 'right' }}>{l.occurrences}</td>
            <td style={{ padding: '6px 8px', textAlign: 'right' }}>{Math.round((l.confidence || 0) * 100)}%</td>
            <td style={{ padding: '6px 8px' }}>
              {l.user_confirmed
                ? <span style={{ color: '#15803d' }}>✓</span>
                : <button onClick={() => onConfirm(l)} disabled={busy}
                    style={{ background: '#fff', color: ACCENT, border: `1px solid ${ACCENT}`, borderRadius: 6, padding: '3px 10px', cursor: 'pointer' }}>
                    Confirm
                  </button>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
